import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Play, Pause, Volume2, VolumeX, RotateCcw } from 'lucide-react';
import { TaskyIconButton } from '../tasky';

/**
 * 楽曲試聴用のミニプレーヤー。
 *
 * ネイティブ `<audio controls>` を使わず、Taskyトークンのみで
 * 再生/一時停止・疑似波形表示・シークバー・音量・経過/総時間表示を組む（v3でmockup.html準拠に拡張）。
 * HTMLAudioElement 自体は ref 経由で内部生成し、DOM 上にはネイティブコントロールを出さない。
 *
 * 波形はサーバーから取得できないため、`src` から決定的に疑似生成する（同じ曲は常に同じ見た目になる）。
 */

export interface SongPlayerProps {
  src: string;
  className?: string;
}

const formatTime = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const BAR_COUNT = 48;

/** src文字列から決定的な疑似波形バー（0〜1の高さ配列）を生成する */
const buildWaveform = (src: string): number[] => {
  let seed = 0;
  for (let i = 0; i < src.length; i += 1) {
    seed = (seed * 31 + src.charCodeAt(i)) >>> 0;
  }
  const bars: number[] = [];
  let state = seed || 1;
  for (let i = 0; i < BAR_COUNT; i += 1) {
    state = (state * 1103515245 + 12345) >>> 0;
    const r = (state % 1000) / 1000;
    bars.push(0.2 + r * 0.8);
  }
  return bars;
};

const SongPlayer: React.FC<SongPlayerProps> = ({ src, className }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);

  const bars = useMemo(() => buildWaveform(src), [src]);

  useEffect(() => {
    const audio = new Audio(src);
    audio.volume = volume;
    audioRef.current = audio;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => setDuration(audio.duration || 0);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.pause();
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      void audio.play();
      setIsPlaying(true);
    }
  };

  const seekTo = (value: number) => {
    const audio = audioRef.current;
    const clamped = Math.max(0, Math.min(duration || value, value));
    setCurrentTime(clamped);
    if (audio) {
      audio.currentTime = clamped;
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => seekTo(Number(e.target.value));

  const handleRestart = () => {
    seekTo(0);
    const audio = audioRef.current;
    if (audio && !isPlaying) {
      void audio.play();
      setIsPlaying(true);
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value) / 100;
    setVolume(value);
    if (audioRef.current) audioRef.current.volume = value;
  };

  const handleWaveformClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    seekTo(ratio * duration);
  };

  const progressRatio = duration > 0 ? currentTime / duration : 0;
  const activeBarCount = Math.round(progressRatio * BAR_COUNT);

  return (
    <div
      data-testid="song-player"
      className={`bg-white border border-warm-border rounded-tasky-md p-3 ${className ?? ''}`}
    >
      <div className="flex items-center gap-3 mb-2">
        <TaskyIconButton
          type="button"
          aria-label={isPlaying ? '一時停止' : '再生'}
          onClick={togglePlay}
          size="md"
        >
          {isPlaying ? <Pause className="fill-current" /> : <Play className="fill-current" />}
        </TaskyIconButton>
        <button
          type="button"
          aria-label="最初から再生"
          onClick={handleRestart}
          className="text-gray-400 hover:text-gray-600 transition-colors shrink-0"
        >
          <RotateCcw className="w-4 h-4" />
        </button>
        <span className="text-xs text-gray-500 tabular-nums w-9 text-right shrink-0">
          {formatTime(currentTime)}
        </span>

        {/* 疑似波形（クリックでシーク可） */}
        <div
          role="presentation"
          onClick={handleWaveformClick}
          className="flex-1 flex items-end gap-[2px] h-8 cursor-pointer select-none"
        >
          {bars.map((h, i) => (
            <div
              key={i}
              className={`flex-1 rounded-sm transition-colors ${
                i < activeBarCount ? 'bg-primary' : 'bg-warm-border'
              }`}
              style={{ height: `${Math.round(h * 100)}%` }}
            />
          ))}
        </div>

        <span className="text-xs text-gray-500 tabular-nums w-9 shrink-0">{formatTime(duration)}</span>
      </div>

      <div className="flex items-center gap-3">
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={0.1}
          value={currentTime}
          onChange={handleSeek}
          className="flex-1 h-1.5 accent-primary cursor-pointer"
          aria-label="再生位置"
        />
        <div className="flex items-center gap-1.5 shrink-0">
          {volume === 0 ? (
            <VolumeX className="w-4 h-4 text-gray-400" />
          ) : (
            <Volume2 className="w-4 h-4 text-gray-400" />
          )}
          <input
            type="range"
            min={0}
            max={100}
            value={Math.round(volume * 100)}
            onChange={handleVolumeChange}
            className="w-16 h-1.5 accent-primary cursor-pointer"
            aria-label="音量"
          />
        </div>
      </div>
    </div>
  );
};

export default SongPlayer;
